// Backend integration point: Replace this static array with an API call to fetch questions
// from a CMS or database, filtered by category and randomized per session

export interface QuizQuestion {
  id: string;
  category: string;
  categorySlug: string;
  difficulty: 'foundation' | 'standard' | 'advanced';
  question: string;
  options: { id: string; text: string }[];
  correctOptionId: string;
  explanation: string;
  ruleReference: string;
  learningTakeaway: string;
}

export const PASS_MARK = 43;
export const TOTAL_QUESTIONS = 50;

export const QUIZ_QUESTIONS: QuizQuestion[] = [
  // ─── ROAD SIGNS (Q1–Q5) ───────────────────────────────────────────────────
  {
    id: 'q-001',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'foundation',
    question: 'What shape are warning signs used on UK roads?',
    options: [
      { id: 'q-001-a', text: 'Circular with a red border' },
      { id: 'q-001-b', text: 'Triangular with a red border' },
      { id: 'q-001-c', text: 'Rectangular with a blue background' },
      { id: 'q-001-d', text: 'Octagonal with a red background' },
    ],
    correctOptionId: 'q-001-b',
    explanation: 'Warning signs in the UK are triangular with a red border on a white background. They alert drivers to hazards ahead such as bends, junctions, pedestrian crossings, and road works. The only exception is the STOP sign (octagonal) and GIVE WAY (inverted triangle).',
    ruleReference: 'Highway Code — Road signs: triangular signs with red borders are warning signs alerting you to hazards.',
    learningTakeaway: 'The three basic shapes: triangle = warning, circle = order (prohibition or instruction), rectangle = information. STOP and GIVE WAY are the two exceptions to learn separately.',
  },
  {
    id: 'q-002',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'foundation',
    question: 'A circular road sign with a red border and a number inside it indicates what type of restriction?',
    options: [
      { id: 'q-002-a', text: 'A recommended speed in mph' },
      { id: 'q-002-b', text: 'A maximum speed limit in mph' },
      { id: 'q-002-c', text: 'A minimum speed limit in mph' },
      { id: 'q-002-d', text: 'A warning of a hazard ahead' },
    ],
    correctOptionId: 'q-002-b',
    explanation: 'A circular sign with a red border and a number inside is a mandatory maximum speed limit sign. You must not exceed the speed shown. These signs impose local speed limits that differ from the national speed limit for that road type.',
    ruleReference: 'Traffic Signs Regulations and General Directions 2016 — Circular signs with red borders are mandatory prohibitions or restrictions.',
    learningTakeaway: 'Circular signs give orders. A red border means prohibition. A blue background means a positive instruction (e.g. minimum speed). Triangular signs warn. Rectangular signs inform.',
  },
  {
    id: 'q-003',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'standard',
    question: 'What does a red circle with a white horizontal bar across it mean?',
    options: [
      { id: 'q-003-a', text: 'No stopping at any time' },
      { id: 'q-003-b', text: 'No entry for vehicular traffic' },
      { id: 'q-003-c', text: 'No overtaking' },
      { id: 'q-003-d', text: 'Road closed ahead' },
    ],
    correctOptionId: 'q-003-b',
    explanation: 'A red circle containing a white horizontal bar is the "No Entry" sign. It means no vehicular traffic may enter from this direction. It is commonly used at the exit of one-way streets or at prohibited access points. Entering against a No Entry sign is a criminal offence.',
    ruleReference: 'Traffic Signs Regulations and General Directions 2016 — No entry sign: red circle with white horizontal bar. Mandatory prohibition on entry for vehicular traffic.',
    learningTakeaway: 'Do not confuse "No Entry" with "No Through Road" (rectangular information sign). No Entry is a mandatory prohibition — you must not enter.',
  },
  {
    id: 'q-004',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'standard',
    question: 'A blue circular sign with a white arrow pointing upwards means what?',
    options: [
      { id: 'q-004-a', text: 'You must not proceed in this direction' },
      { id: 'q-004-b', text: 'This is a recommended route for this direction' },
      { id: 'q-004-c', text: 'You must proceed ahead only — no turning allowed' },
      { id: 'q-004-d', text: 'A one-way street begins here' },
    ],
    correctOptionId: 'q-004-c',
    explanation: 'A blue circular sign with a white arrow pointing upwards means "ahead only" — you must proceed straight ahead and are not permitted to turn left or right at that junction. Blue circular signs give positive mandatory instructions.',
    ruleReference: 'Traffic Signs Regulations and General Directions 2016 — Blue circular signs are mandatory instructions (positive orders). Ahead only sign means drivers must go straight on.',
    learningTakeaway: 'Blue circles = positive mandatory instructions (you must do this). Red circles = negative mandatory restrictions (you must not do this). Common blue circle signs: turn left ahead, keep right, mini-roundabout.',
  },
  {
    id: 'q-005',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'advanced',
    question: 'What does a white circular sign with a black diagonal stripe indicate?',
    options: [
      { id: 'q-005-a', text: 'No vehicles allowed' },
      { id: 'q-005-b', text: 'End of all restrictions' },
      { id: 'q-005-c', text: 'The national speed limit applies' },
      { id: 'q-005-d', text: 'No overtaking zone ends' },
    ],
    correctOptionId: 'q-005-c',
    explanation: 'The white circular sign with a black diagonal stripe is the national speed limit sign. It means the national speed limit applies for that road type: 60 mph on a single carriageway, 70 mph on a dual carriageway or motorway. No number is shown because it is the national default.',
    ruleReference: 'Highway Code Rule 124 — The national speed limit sign (white circle, diagonal stripe) indicates the national speed limit applies.',
    learningTakeaway: 'National speed limit sign = 60 mph on single carriageway, 70 mph on dual carriageway/motorway. Always check the road type to know which limit applies.',
  },

  // ─── SPEED LIMITS (Q6–Q9) ─────────────────────────────────────────────────
  {
    id: 'q-006',
    category: 'Speed Limits',
    categorySlug: 'speed-limits',
    difficulty: 'foundation',
    question: 'What is the national speed limit for a car on a motorway in the United Kingdom?',
    options: [
      { id: 'q-006-a', text: '60 mph' },
      { id: 'q-006-b', text: '70 mph' },
      { id: 'q-006-c', text: '80 mph' },
      { id: 'q-006-d', text: '50 mph' },
    ],
    correctOptionId: 'q-006-b',
    explanation: 'The national speed limit on a motorway for cars and motorcycles is 70 mph. This applies unless a lower variable speed limit is displayed on overhead gantry signs. Exceeding this limit is a criminal offence under the Road Traffic Regulation Act 1984.',
    ruleReference: 'Highway Code Rule 260 — National speed limit for motorways is 70 mph for cars and motorcycles.',
    learningTakeaway: 'Always check overhead gantry signs on motorways — variable speed limits (e.g. 50 or 60 mph) are legally enforceable when displayed.',
  },
  {
    id: 'q-007',
    category: 'Speed Limits',
    categorySlug: 'speed-limits',
    difficulty: 'standard',
    question: 'What is the national speed limit for a car on a single carriageway road outside a built-up area?',
    options: [
      { id: 'q-007-a', text: '50 mph' },
      { id: 'q-007-b', text: '70 mph' },
      { id: 'q-007-c', text: '60 mph' },
      { id: 'q-007-d', text: '40 mph' },
    ],
    correctOptionId: 'q-007-c',
    explanation: 'The national speed limit for a car on a single carriageway road outside a built-up area is 60 mph. This is indicated by the national speed limit sign — a white circular sign with a black diagonal stripe.',
    ruleReference: 'Highway Code Rule 124 — National speed limit on single carriageway roads for cars is 60 mph.',
    learningTakeaway: 'The national speed limit sign means 60 mph on single carriageways and 70 mph on dual carriageways and motorways.',
  },
  {
    id: 'q-008',
    category: 'Speed Limits',
    categorySlug: 'speed-limits',
    difficulty: 'foundation',
    question: 'What is the default speed limit in a built-up area in the UK where no other limit is displayed?',
    options: [
      { id: 'q-008-a', text: '20 mph' },
      { id: 'q-008-b', text: '40 mph' },
      { id: 'q-008-c', text: '30 mph' },
      { id: 'q-008-d', text: '25 mph' },
    ],
    correctOptionId: 'q-008-c',
    explanation: 'The default speed limit in a built-up area (indicated by street lighting at intervals of no more than 185 metres) is 30 mph, unless otherwise signed. Many local authorities have introduced 20 mph zones in residential areas and near schools, but these must be signed.',
    ruleReference: 'Highway Code Rule 124 — The 30 mph limit applies to all traffic on all roads with street lighting unless signs show otherwise.',
    learningTakeaway: 'Street lights = 30 mph default. Always watch for signs indicating 20 mph zones, which are now common in residential areas and near schools across the UK.',
  },
  {
    id: 'q-009',
    category: 'Speed Limits',
    categorySlug: 'speed-limits',
    difficulty: 'standard',
    question: 'You are driving on a road where the street lights are not lit. What does this tell you about the speed limit?',
    options: [
      { id: 'q-009-a', text: 'The speed limit is 40 mph' },
      { id: 'q-009-b', text: 'There is no speed limit' },
      { id: 'q-009-c', text: 'The national speed limit applies' },
      { id: 'q-009-d', text: 'The speed limit is 50 mph' },
    ],
    correctOptionId: 'q-009-c',
    explanation: 'Where street lights are absent or not lit, the national speed limit applies for that road type — 60 mph on a single carriageway or 70 mph on a dual carriageway or motorway. Street lighting set at intervals of no more than 185 metres indicates a 30 mph limit unless signs state otherwise.',
    ruleReference: 'Highway Code Rule 124 — A 30 mph limit usually applies where there are street lights unless signs show otherwise.',
    learningTakeaway: 'Street lights with posts no more than 185 metres apart = 30 mph unless signed otherwise. No lights or unlit lights = national speed limit applies.',
  },

  // ─── TRAFFIC SIGNALS (Q10–Q12) ────────────────────────────────────────────
  {
    id: 'q-010',
    category: 'Traffic Signals',
    categorySlug: 'traffic-signals',
    difficulty: 'foundation',
    question: 'What does a steady amber traffic light mean?',
    options: [
      { id: 'q-010-a', text: 'Proceed if the road is clear' },
      { id: 'q-010-b', text: 'Stop unless it is unsafe to do so' },
      { id: 'q-010-c', text: 'Slow down and prepare to stop — stop unless you have already crossed the stop line' },
      { id: 'q-010-d', text: 'Give way to oncoming traffic' },
    ],
    correctOptionId: 'q-010-c',
    explanation: 'A steady amber light means you must stop at the stop line unless you have already crossed it or are so close to it that stopping would be dangerous. It is not a signal to accelerate through the junction before the light turns red.',
    ruleReference: 'Highway Code Rule 175 — Amber means stop at the stop line. You may go on only if the amber appears after you have crossed the stop line or are so close to it that to pull up might cause an accident.',
    learningTakeaway: 'Amber does not mean "speed up to beat the red." If in doubt, stop safely. Crossing a red light is a serious offence carrying a fine and penalty points.',
  },
  {
    id: 'q-011',
    category: 'Traffic Signals',
    categorySlug: 'traffic-signals',
    difficulty: 'standard',
    question: 'At a pelican crossing, what does a flashing amber light mean to drivers?',
    options: [
      { id: 'q-011-a', text: 'Stop — pedestrians have priority' },
      { id: 'q-011-b', text: 'Give way to pedestrians still on the crossing, then proceed when clear' },
      { id: 'q-011-c', text: 'Proceed — the crossing is clear' },
      { id: 'q-011-d', text: 'The lights are about to change to red' },
    ],
    correctOptionId: 'q-011-b',
    explanation: 'At a pelican crossing, a flashing amber light follows the red light. It means you must give way to any pedestrians who are still on the crossing. Once the crossing is clear, you may proceed. This phase allows pedestrians who started crossing on green to complete their crossing safely.',
    ruleReference: 'Highway Code Rule 196 — At a pelican crossing, a flashing amber means give way to pedestrians on the crossing.',
    learningTakeaway: 'Pelican crossings are the only crossings with a flashing amber phase for drivers. Puffin and toucan crossings use sensors and do not have a flashing amber phase.',
  },
  {
    id: 'q-012',
    category: 'Traffic Signals',
    categorySlug: 'traffic-signals',
    difficulty: 'advanced',
    question: 'What does a green filter arrow at traffic lights mean?',
    options: [
      { id: 'q-012-a', text: 'You may proceed in the direction of the arrow regardless of other signals' },
      { id: 'q-012-b', text: 'You may proceed in the direction of the arrow only if the main light is also green' },
      { id: 'q-012-c', text: 'You must give way to oncoming traffic before proceeding in the arrow direction' },
      { id: 'q-012-d', text: 'The arrow indicates a recommended route only' },
    ],
    correctOptionId: 'q-012-a',
    explanation: 'A green filter arrow means you may proceed in the direction shown, even if the main traffic light is red. Traffic travelling in other directions will be held by red lights. You should still give way to pedestrians crossing the road you are turning into.',
    ruleReference: 'Highway Code Rule 177 — A green filter arrow means you may proceed in the direction shown, even if the main signal is red.',
    learningTakeaway: 'Green filter arrows allow movement in one direction while other traffic is stopped. Always check for pedestrians crossing your path when following a filter arrow.',
  },

  // ─── JUNCTIONS (Q13–Q15) ──────────────────────────────────────────────────
  {
    id: 'q-013',
    category: 'Junctions',
    categorySlug: 'junctions',
    difficulty: 'foundation',
    question: 'At a junction controlled by a GIVE WAY sign, what must you do?',
    options: [
      { id: 'q-013-a', text: 'Stop completely before proceeding' },
      { id: 'q-013-b', text: 'Give way to traffic on the major road and proceed when safe' },
      { id: 'q-013-c', text: 'Sound your horn and proceed if the road appears clear' },
      { id: 'q-013-d', text: 'Flash your headlights and proceed' },
    ],
    correctOptionId: 'q-013-b',
    explanation: 'At a GIVE WAY junction you must give way to traffic on the major road. You do not have to stop completely unless it is necessary to do so safely. The GIVE WAY sign is an inverted red triangle. Double broken white lines across the road mark the give way point.',
    ruleReference: 'Highway Code Rule 172 — At a junction with a GIVE WAY sign or double broken white lines, give way to traffic on the major road.',
    learningTakeaway: 'GIVE WAY ≠ STOP. You must give priority to traffic on the major road but you only need to stop if traffic makes it necessary. STOP signs require a full stop regardless of traffic.',
  },
  {
    id: 'q-014',
    category: 'Junctions',
    categorySlug: 'junctions',
    difficulty: 'standard',
    question: 'When turning right at a crossroads where both you and an oncoming vehicle wish to turn right, what is the safest method?',
    options: [
      { id: 'q-014-a', text: 'Pass offside to offside (right side to right side) before turning' },
      { id: 'q-014-b', text: 'Pass nearside to nearside (left side to left side) before turning' },
      { id: 'q-014-c', text: 'The first vehicle to arrive has priority and should turn first' },
      { id: 'q-014-d', text: 'Both vehicles should stop and wait for a gap in traffic' },
    ],
    correctOptionId: 'q-014-a',
    explanation: 'The Highway Code recommends passing offside to offside (right side to right side) when both vehicles are turning right at a crossroads. This keeps oncoming traffic in view throughout the manoeuvre. However, road markings or the layout of the junction may require nearside to nearside — always follow road markings.',
    ruleReference: 'Highway Code Rule 179 — When turning right at a crossroads where an oncoming vehicle is also turning right, it is generally safer to pass offside to offside.',
    learningTakeaway: 'Offside-to-offside is the recommended method as it keeps oncoming traffic visible. Always check for road markings that may dictate the method at a specific junction.',
  },
  {
    id: 'q-015',
    category: 'Junctions',
    categorySlug: 'junctions',
    difficulty: 'advanced',
    question: 'You are waiting to emerge from a side road. A large vehicle is approaching from the right with its left indicator flashing. What should you do?',
    options: [
      { id: 'q-015-a', text: 'Emerge immediately as the vehicle is clearly turning left' },
      { id: 'q-015-b', text: 'Wait until the vehicle has actually started to turn before emerging' },
      { id: 'q-015-c', text: 'Sound your horn to confirm the driver intends to turn' },
      { id: 'q-015-d', text: 'Flash your headlights to indicate you are waiting' },
    ],
    correctOptionId: 'q-015-b',
    explanation: 'You should wait until the vehicle has actually started to turn before emerging. Drivers sometimes leave indicators on by mistake or may change their mind. Emerging too early based solely on an indicator could result in a collision. Only move when you are certain it is safe.',
    ruleReference: 'Highway Code Rule 170 — Do not emerge based solely on another driver\'s indicator. Wait until you are certain it is safe.',
    learningTakeaway: 'Indicators show intention, not action. A large vehicle may also swing wide before turning. Always wait for the vehicle to commit to the turn before you emerge.',
  },

  // ─── ROUNDABOUTS (Q16–Q18) ────────────────────────────────────────────────
  {
    id: 'q-016',
    category: 'Roundabouts',
    categorySlug: 'roundabouts',
    difficulty: 'foundation',
    question: 'When approaching a roundabout, who has priority?',
    options: [
      { id: 'q-016-a', text: 'Vehicles already on the roundabout' },
      { id: 'q-016-b', text: 'Vehicles approaching from the right' },
      { id: 'q-016-c', text: 'Vehicles approaching from the left' },
      { id: 'q-016-d', text: 'The largest vehicle on the roundabout' },
    ],
    correctOptionId: 'q-016-a',
    explanation: 'At a roundabout you must give way to traffic already on the roundabout (coming from your right). You should only enter the roundabout when there is a safe gap in the traffic. Some roundabouts have traffic lights or GIVE WAY signs that modify this rule.',
    ruleReference: 'Highway Code Rule 185 — At a roundabout, give way to traffic already on the roundabout (from your right) unless road markings indicate otherwise.',
    learningTakeaway: 'Traffic on the roundabout has priority. Watch for road markings — some roundabouts have lane markings that give priority to entering traffic in specific lanes.',
  },
  {
    id: 'q-017',
    category: 'Roundabouts',
    categorySlug: 'roundabouts',
    difficulty: 'standard',
    question: 'When taking the second exit at a roundabout, which lane should you normally use on approach?',
    options: [
      { id: 'q-017-a', text: 'Always the right-hand lane' },
      { id: 'q-017-b', text: 'The left-hand lane unless road markings indicate otherwise' },
      { id: 'q-017-c', text: 'Either lane — it makes no difference' },
      { id: 'q-017-d', text: 'The left-hand lane only if turning left' },
    ],
    correctOptionId: 'q-017-b',
    explanation: 'For the second exit (going straight ahead) at a roundabout, you should normally approach in the left-hand lane unless road markings or signs indicate otherwise. Signal left after passing the exit before the one you intend to take. Always follow any road markings at the specific roundabout.',
    ruleReference: 'Highway Code Rule 186 — When going straight ahead at a roundabout, approach in the left-hand lane unless signs or markings indicate otherwise.',
    learningTakeaway: 'Always check road markings on approach to a roundabout — they override the general guidance. Signal left after passing the exit before your intended exit.',
  },
  {
    id: 'q-018',
    category: 'Roundabouts',
    categorySlug: 'roundabouts',
    difficulty: 'advanced',
    question: 'At a mini-roundabout, what must you do?',
    options: [
      { id: 'q-018-a', text: 'Give way to all traffic and pass around the central markings' },
      { id: 'q-018-b', text: 'Drive over the central markings if necessary' },
      { id: 'q-018-c', text: 'Give way only to vehicles on your right' },
      { id: 'q-018-d', text: 'Sound your horn before entering' },
    ],
    correctOptionId: 'q-018-a',
    explanation: 'At a mini-roundabout you must give way to traffic already on the roundabout and pass around the central markings. Unlike a full roundabout, the central marking at a mini-roundabout may be driven over if necessary (e.g. by large vehicles), but you should pass around it where possible.',
    ruleReference: 'Highway Code Rule 188 — At mini-roundabouts, give way to traffic from the right and pass around the central markings.',
    learningTakeaway: 'Mini-roundabouts follow the same priority rules as full roundabouts. The central dome may be driven over by large vehicles when necessary, but should be passed around by cars.',
  },

  // ─── PEDESTRIAN CROSSINGS (Q19–Q21) ───────────────────────────────────────
  {
    id: 'q-019',
    category: 'Pedestrian Crossings',
    categorySlug: 'pedestrian-crossings',
    difficulty: 'foundation',
    question: 'At a zebra crossing, when must you give way to pedestrians?',
    options: [
      { id: 'q-019-a', text: 'Only when pedestrians are in the middle of the crossing' },
      { id: 'q-019-b', text: 'When pedestrians have stepped onto the crossing' },
      { id: 'q-019-c', text: 'Only when a lollipop person is present' },
      { id: 'q-019-d', text: 'Only during school hours' },
    ],
    correctOptionId: 'q-019-b',
    explanation: 'At a zebra crossing you must give way to pedestrians who have stepped onto the crossing. You should also slow down and be prepared to stop for pedestrians waiting at the crossing. Never wave pedestrians across or flash your lights — another vehicle may not see them.',
    ruleReference: 'Highway Code Rule 195 — You MUST give way to pedestrians on a zebra crossing. Do not wave them across or flash your lights.',
    learningTakeaway: 'Approach zebra crossings with reduced speed. Never beckon pedestrians to cross — another vehicle may not see them. Give cyclists and horse riders the same consideration.',
  },
  {
    id: 'q-020',
    category: 'Pedestrian Crossings',
    categorySlug: 'pedestrian-crossings',
    difficulty: 'standard',
    question: 'What type of crossing allows cyclists to cross alongside pedestrians?',
    options: [
      { id: 'q-020-a', text: 'Pelican crossing' },
      { id: 'q-020-b', text: 'Puffin crossing' },
      { id: 'q-020-c', text: 'Toucan crossing' },
      { id: 'q-020-d', text: 'Pegasus crossing' },
    ],
    correctOptionId: 'q-020-c',
    explanation: 'A toucan crossing (two can cross) is designed for both pedestrians and cyclists to cross together. It is wider than a pelican crossing and has a green cycle symbol alongside the green man. Cyclists may ride across rather than dismount.',
    ruleReference: 'Highway Code Rule 80 — Toucan crossings are shared by pedestrians and cyclists. Cyclists may ride across.',
    learningTakeaway: 'Crossing types: Zebra (no lights), Pelican (push button, flashing amber), Puffin (sensors, no flashing amber), Toucan (pedestrians + cyclists), Pegasus (equestrians). Each has different rules.',
  },
  {
    id: 'q-021',
    category: 'Pedestrian Crossings',
    categorySlug: 'pedestrian-crossings',
    difficulty: 'advanced',
    question: 'You are approaching a school crossing patrol (lollipop person) who has stepped out and raised their sign. What must you do?',
    options: [
      { id: 'q-021-a', text: 'Slow down and proceed carefully' },
      { id: 'q-021-b', text: 'Stop and wait until the patrol has returned to the pavement and the road is clear' },
      { id: 'q-021-c', text: 'Sound your horn to warn the patrol you are approaching' },
      { id: 'q-021-d', text: 'Stop only if children are actually crossing' },
    ],
    correctOptionId: 'q-021-b',
    explanation: 'When a school crossing patrol raises their sign you must stop. You must not move off until the patrol has returned to the pavement and the road is clear. Failing to stop for a school crossing patrol is a criminal offence.',
    ruleReference: 'Highway Code Rule 209 — You MUST stop when a school crossing patrol shows a stop sign. Do not move until they have returned to the pavement.',
    learningTakeaway: 'School crossing patrols have the legal authority to stop traffic. Treat their sign the same as a red traffic light — you must stop and wait.',
  },

  // ─── MOTORWAYS (Q22–Q24) ──────────────────────────────────────────────────
  {
    id: 'q-022',
    category: 'Motorways',
    categorySlug: 'motorways',
    difficulty: 'foundation',
    question: 'Which of the following vehicles is NOT permitted to use a motorway?',
    options: [
      { id: 'q-022-a', text: 'Motorcycles over 50cc' },
      { id: 'q-022-b', text: 'Agricultural tractors' },
      { id: 'q-022-c', text: 'Coaches' },
      { id: 'q-022-d', text: 'HGVs' },
    ],
    correctOptionId: 'q-022-b',
    explanation: 'Agricultural tractors are not permitted to use motorways. Other prohibited vehicles include motorcycles under 50cc, invalid carriages, and certain slow-moving vehicles. Learner drivers were permitted on motorways from June 2018, but only when accompanied by an approved driving instructor in a vehicle with dual controls.',
    ruleReference: 'Highway Code Rule 253 — Motorways must not be used by pedestrians, cyclists, motorcycles under 50cc, agricultural vehicles, and certain other vehicles.',
    learningTakeaway: 'Know which vehicles are prohibited from motorways. Learner drivers may now use motorways with an ADI in a dual-control vehicle — this changed in June 2018.',
  },
  {
    id: 'q-023',
    category: 'Motorways',
    categorySlug: 'motorways',
    difficulty: 'standard',
    question: 'On a motorway, you wish to overtake. Which lane should you use and what should you do afterwards?',
    options: [
      { id: 'q-023-a', text: 'Use any lane that is clear, then stay in that lane' },
      { id: 'q-023-b', text: 'Use the right-hand lane to overtake, then return to the left when safe' },
      { id: 'q-023-c', text: 'Use the middle lane as it is the designated overtaking lane' },
      { id: 'q-023-d', text: 'Use the hard shoulder if the other lanes are busy' },
    ],
    correctOptionId: 'q-023-b',
    explanation: 'On a motorway you should always drive in the left-hand lane when the road ahead is clear. Use the right-hand lane (or middle lane on a three-lane motorway) only to overtake, then move back to the left when it is safe to do so. Driving in the middle lane when the left is clear is "middle lane hogging" and is an offence.',
    ruleReference: 'Highway Code Rule 264 — Keep left on motorways. Use the right-hand lane only for overtaking. Return to the left as soon as it is safe.',
    learningTakeaway: 'Middle lane hogging is a fixed penalty offence (£100 fine, 3 penalty points). Always return to the left-hand lane after overtaking.',
  },
  {
    id: 'q-024',
    category: 'Motorways',
    categorySlug: 'motorways',
    difficulty: 'advanced',
    question: 'What does a red X on a motorway overhead gantry sign mean?',
    options: [
      { id: 'q-024-a', text: 'Caution — hazard ahead in that lane' },
      { id: 'q-024-b', text: 'That lane is closed — you must not use it' },
      { id: 'q-024-c', text: 'Reduce speed to 50 mph in that lane' },
      { id: 'q-024-d', text: 'Overtaking is prohibited in that lane' },
    ],
    correctOptionId: 'q-024-b',
    explanation: 'A red X on a motorway overhead gantry sign means that lane is closed and you must not drive in it. Lane closures are used to manage incidents, road works, or hazards. Driving in a lane marked with a red X is a criminal offence and can result in a fixed penalty.',
    ruleReference: 'Highway Code Rule 258 — A red X on a motorway overhead sign means the lane is closed. You must not drive in that lane.',
    learningTakeaway: 'Red X = lane closed. This is a mandatory signal, not advisory. Move out of the closed lane in good time. On smart motorways, the hard shoulder may also be shown as open or closed by overhead signs.',
  },

  // ─── LANE DISCIPLINE (Q25–Q27) ────────────────────────────────────────────
  {
    id: 'q-025',
    category: 'Lane Discipline',
    categorySlug: 'lane-discipline',
    difficulty: 'standard',
    question: 'What does a solid white line in the centre of the road mean?',
    options: [
      { id: 'q-025-a', text: 'You may cross it to overtake if it is safe' },
      { id: 'q-025-b', text: 'You must not cross or straddle it unless entering premises or passing a stationary obstruction' },
      { id: 'q-025-c', text: 'It marks the edge of the carriageway' },
      { id: 'q-025-d', text: 'It indicates a bus lane' },
    ],
    correctOptionId: 'q-025-b',
    explanation: 'A solid white line in the centre of the road means you must not cross or straddle it. Exceptions include entering premises, passing a stationary obstruction, or when directed by a police officer or traffic warden. A solid line on your side means you must not cross; a broken line on your side means you may cross if safe.',
    ruleReference: 'Highway Code Rule 128 — A solid white line means you must not cross or straddle it unless entering premises, passing a stationary obstruction, or directed by police.',
    learningTakeaway: 'Solid white centre line = no crossing. Broken white centre line = may cross if safe. Double white lines: if the line nearest you is solid, you must not cross.',
  },
  {
    id: 'q-026',
    category: 'Lane Discipline',
    categorySlug: 'lane-discipline',
    difficulty: 'foundation',
    question: 'What does a yellow box junction painted on the road mean?',
    options: [
      { id: 'q-026-a', text: 'You must not enter the box at any time' },
      { id: 'q-026-b', text: 'You must not enter the box unless your exit road is clear, except when turning right' },
      { id: 'q-026-c', text: 'You may stop in the box briefly while waiting to turn' },
      { id: 'q-026-d', text: 'The box marks a pedestrian priority zone' },
    ],
    correctOptionId: 'q-026-b',
    explanation: 'You must not enter a yellow box junction unless your exit road is clear and you can drive straight through without stopping. The only exception is when you are turning right and are prevented from doing so by oncoming traffic — you may wait in the box in this case.',
    ruleReference: 'Highway Code Rule 174 — You MUST NOT enter a box junction unless your exit road is clear. Exception: you may enter when turning right if only oncoming traffic or other right-turning vehicles prevent you from completing the turn.',
    learningTakeaway: 'Yellow box junctions keep junctions clear. The right-turn exception is the only time you may stop in the box. Entering a box and stopping (other than for a right turn) can result in a penalty.',
  },
  {
    id: 'q-027',
    category: 'Lane Discipline',
    categorySlug: 'lane-discipline',
    difficulty: 'advanced',
    question: 'What are the white diagonal stripes or chevrons painted on the road used for?',
    options: [
      { id: 'q-027-a', text: 'To separate traffic streams and protect turning vehicles' },
      { id: 'q-027-b', text: 'To indicate a bus lane' },
      { id: 'q-027-c', text: 'To mark a pedestrian crossing area' },
      { id: 'q-027-d', text: 'To indicate a cycle lane' },
    ],
    correctOptionId: 'q-027-a',
    explanation: 'White diagonal stripes or chevrons are used to separate traffic streams and protect vehicles that are turning. If the area is bordered by a broken white line, you should not enter it unless necessary and it is safe. If bordered by a solid white line, you must not enter it except in an emergency.',
    ruleReference: 'Highway Code Rule 130 — White diagonal stripes or chevrons separate traffic streams. Do not enter if bordered by solid lines except in emergency.',
    learningTakeaway: 'Broken border = avoid if possible but may enter if safe. Solid border = must not enter except in emergency. These markings are common on dual carriageways and at complex junctions.',
  },

  // ─── HAZARD AWARENESS (Q28–Q31) ───────────────────────────────────────────
  {
    id: 'q-028',
    category: 'Hazard Awareness',
    categorySlug: 'hazard-awareness',
    difficulty: 'standard',
    question: 'You are driving in heavy rain and your vehicle aquaplanes. What is the correct action?',
    options: [
      { id: 'q-028-a', text: 'Brake firmly to slow the vehicle as quickly as possible' },
      { id: 'q-028-b', text: 'Steer sharply to one side to regain grip' },
      { id: 'q-028-c', text: 'Ease off the accelerator and hold the steering wheel straight until grip returns' },
      { id: 'q-028-d', text: 'Apply the handbrake to slow down gently' },
    ],
    correctOptionId: 'q-028-c',
    explanation: 'When aquaplaning, the tyres ride on a layer of water and lose contact with the road surface. Braking or steering sharply can cause the vehicle to skid or spin. The correct response is to ease off the accelerator smoothly and hold the steering straight. As the vehicle slows, the tyres will regain contact with the road.',
    ruleReference: 'Highway Code Rule 122 — In wet conditions, stopping distances are at least doubled. Ease off the accelerator if aquaplaning occurs.',
    learningTakeaway: 'Aquaplaning is most likely at speeds above 50 mph in standing water. Correctly inflated tyres with adequate tread depth significantly reduce aquaplaning risk.',
  },
  {
    id: 'q-029',
    category: 'Hazard Awareness',
    categorySlug: 'hazard-awareness',
    difficulty: 'standard',
    question: 'You see a cyclist ahead who is riding close to the centre of a narrow lane. What should you do?',
    options: [
      { id: 'q-029-a', text: 'Sound your horn to ask the cyclist to move to the left' },
      { id: 'q-029-b', text: 'Overtake immediately as cyclists should not obstruct traffic' },
      { id: 'q-029-c', text: 'Be patient, slow down, and wait until it is safe to pass with adequate clearance' },
      { id: 'q-029-d', text: 'Flash your headlights to signal your intention to overtake' },
    ],
    correctOptionId: 'q-029-c',
    explanation: 'Cyclists may ride in the centre of a lane (primary position) to make themselves more visible and to prevent unsafe overtaking. This is legal and correct behaviour. The Highway Code advises drivers to give cyclists at least 1.5 metres of space when overtaking at speeds up to 30 mph, and more at higher speeds.',
    ruleReference: 'Highway Code Rule 163 — Give cyclists at least 1.5 metres when overtaking at up to 30 mph. Give more space at higher speeds.',
    learningTakeaway: 'Cyclists in the primary position are protecting themselves. Updated Highway Code rules (2022) give cyclists priority in many situations. Never use the horn aggressively near vulnerable road users.',
  },
  {
    id: 'q-030',
    category: 'Hazard Awareness',
    categorySlug: 'hazard-awareness',
    difficulty: 'advanced',
    question: 'You are driving at night on an unlit road using full-beam headlights. An oncoming vehicle appears. When must you dip your headlights?',
    options: [
      { id: 'q-030-a', text: 'Only if the other driver flashes their lights first' },
      { id: 'q-030-b', text: 'Switch to dipped headlights in good time before meeting the oncoming vehicle' },
      { id: 'q-030-c', text: 'Only if the road has a central white line' },
      { id: 'q-030-d', text: 'Dip headlights only if the oncoming vehicle is within 50 metres' },
    ],
    correctOptionId: 'q-030-b',
    explanation: 'You must dip your headlights in good time before meeting an oncoming vehicle to avoid dazzling the other driver. You should also dip headlights when following another vehicle closely, as full-beam can dazzle through their mirrors.',
    ruleReference: 'Highway Code Rule 114 — You MUST NOT use full-beam headlights so as to dazzle oncoming or preceding drivers or riders. Dip in good time.',
    learningTakeaway: 'Switch back to full-beam after the oncoming vehicle has passed, but only when you are sure it will not dazzle them. Full-beam significantly extends your vision — use it when safe on unlit roads.',
  },
  {
    id: 'q-031',
    category: 'Hazard Awareness',
    categorySlug: 'hazard-awareness',
    difficulty: 'standard',
    question: 'What is the typical overall stopping distance for a car travelling at 50 mph on a dry road?',
    options: [
      { id: 'q-031-a', text: '36 metres (118 feet)' },
      { id: 'q-031-b', text: '53 metres (175 feet)' },
      { id: 'q-031-c', text: '73 metres (240 feet)' },
      { id: 'q-031-d', text: '96 metres (315 feet)' },
    ],
    correctOptionId: 'q-031-b',
    explanation: 'At 50 mph, the typical overall stopping distance is 53 metres (175 feet). This comprises a thinking distance of 15 metres (50 feet) and a braking distance of 38 metres (125 feet). These figures assume dry roads and a vehicle in good condition.',
    ruleReference: 'Highway Code Rule 126 — Overall stopping distances: thinking distance plus braking distance. At 50 mph: 53 metres total.',
    learningTakeaway: 'Remember the stopping distances table from the Highway Code. Wet roads can double braking distance. The "two-second rule" is a minimum safe following distance in dry conditions.',
  },

  // ─── SAFE DRIVING PRACTICES (Q32–Q34) ────────────────────────────────────
  {
    id: 'q-032',
    category: 'Safe Driving Practices',
    categorySlug: 'safe-driving',
    difficulty: 'foundation',
    question: 'When must you use your vehicle\'s headlights?',
    options: [
      { id: 'q-032-a', text: 'Only when driving on motorways at night' },
      { id: 'q-032-b', text: 'Between sunset and sunrise and in seriously reduced visibility' },
      { id: 'q-032-c', text: 'Only when driving in fog' },
      { id: 'q-032-d', text: 'Only when other drivers flash their lights at you' },
    ],
    correctOptionId: 'q-032-b',
    explanation: 'You must use headlights between sunset and sunrise. You must also use them (or front fog lights) when visibility is seriously reduced — generally when you cannot see for more than 100 metres (328 feet). Daytime running lights do not replace headlights in these conditions.',
    ruleReference: 'Highway Code Rule 113 — You MUST use headlights when visibility is seriously reduced and between sunset and sunrise. Road Vehicles Lighting Regulations 1989.',
    learningTakeaway: 'Use headlights in poor visibility — not just at night. Fog lights are for when visibility drops below 100 metres. Remember to switch fog lights off when visibility improves to avoid dazzling other drivers.',
  },
  {
    id: 'q-033',
    category: 'Safe Driving Practices',
    categorySlug: 'safe-driving',
    difficulty: 'standard',
    question: 'When are you permitted to use a vehicle\'s horn?',
    options: [
      { id: 'q-033-a', text: 'To greet a friend you see on the pavement' },
      { id: 'q-033-b', text: 'Only to warn others of your presence when necessary to avoid a collision' },
      { id: 'q-033-c', text: 'To warn a slow driver ahead that you wish to overtake' },
      { id: 'q-033-d', text: 'Whenever you feel it is helpful to alert other road users' },
    ],
    correctOptionId: 'q-033-b',
    explanation: 'The horn must only be used while the vehicle is moving and only to warn other road users of your presence. It must not be used in a built-up area between 11:30 pm and 7:00 am, and it must not be used when stationary (except in cases of danger from a moving vehicle).',
    ruleReference: 'Highway Code Rule 112 — Only use your horn to warn others of your presence. Never use it aggressively. Do not use it in a stationary vehicle or in built-up areas between 11:30 pm and 7:00 am.',
    learningTakeaway: 'The horn is a safety warning device, not a communication tool. It must not be used to express frustration, greet friends, or pressure other drivers.',
  },
  {
    id: 'q-034',
    category: 'Safe Driving Practices',
    categorySlug: 'safe-driving',
    difficulty: 'advanced',
    question: 'In wet weather, how does the Highway Code advise you to adjust your following distance?',
    options: [
      { id: 'q-034-a', text: 'Increase it by one third' },
      { id: 'q-034-b', text: 'Double it' },
      { id: 'q-034-c', text: 'Triple it for speeds above 50 mph' },
      { id: 'q-034-d', text: 'Keep the same distance but reduce speed by 10 mph' },
    ],
    correctOptionId: 'q-034-b',
    explanation: 'The Highway Code states that in wet weather, stopping distances are at least doubled compared to dry conditions. You should at least double the gap between your vehicle and the vehicle in front. In icy or snowy conditions, stopping distances can increase tenfold.',
    ruleReference: 'Highway Code Rule 126 — In wet weather, stopping distances are at least double those required in dry conditions.',
    learningTakeaway: 'In ice and snow, stopping distances can be ten times greater than in dry conditions. Adjust your speed and following distance dramatically in adverse weather.',
  },

  // ─── VEHICLE SAFETY (Q35–Q37) ─────────────────────────────────────────────
  {
    id: 'q-035',
    category: 'Vehicle Safety',
    categorySlug: 'vehicle-safety',
    difficulty: 'foundation',
    question: 'What is the legal minimum tread depth for car tyres in the UK?',
    options: [
      { id: 'q-035-a', text: '1.0 mm across the central three-quarters of the tyre' },
      { id: 'q-035-b', text: '1.6 mm across the central three-quarters of the tyre' },
      { id: 'q-035-c', text: '2.0 mm across the full width of the tyre' },
      { id: 'q-035-d', text: '3.0 mm across the central three-quarters of the tyre' },
    ],
    correctOptionId: 'q-035-b',
    explanation: 'Car tyres must have a minimum tread depth of 1.6 mm across the central three-quarters of the tread width and around the entire circumference. Driving with tyres below this limit is a criminal offence and can result in a fine of up to £2,500 and three penalty points per tyre.',
    ruleReference: 'Road Vehicles (Construction and Use) Regulations 1986 — Minimum tyre tread depth of 1.6mm for cars across the central 75% of tread.',
    learningTakeaway: 'Many tyre manufacturers recommend replacing tyres at 3mm, as stopping distances increase significantly below this level. Use tread wear indicators moulded into tyre grooves to check.',
  },
  {
    id: 'q-036',
    category: 'Vehicle Safety',
    categorySlug: 'vehicle-safety',
    difficulty: 'standard',
    question: 'Before starting a long motorway journey, what tyre check is recommended?',
    options: [
      { id: 'q-036-a', text: 'Check tyre pressure only when the tyres are warm from driving' },
      { id: 'q-036-b', text: 'Check tread depth, condition, and tyre pressure when tyres are cold' },
      { id: 'q-036-c', text: 'Only inspect the two front tyres as they bear the most load' },
      { id: 'q-036-d', text: 'Tyre checks are only needed if a warning light illuminates' },
    ],
    correctOptionId: 'q-036-b',
    explanation: 'You should check tyre tread depth (minimum 1.6 mm), tyre condition (for cuts, bulges, or embedded objects), and tyre pressure when the tyres are cold — before the journey. Tyre pressure increases as tyres warm up during driving, so checking hot tyres gives an inaccurate reading.',
    ruleReference: 'Highway Code Rule 238 — Check tyres are in good condition and inflated to the correct pressure before any journey, especially long ones.',
    learningTakeaway: 'Always check tyre pressure when tyres are cold (the car has been stationary for at least three hours). Refer to your vehicle handbook for the correct pressure — it may differ for front and rear tyres and for a fully loaded vehicle.',
  },
  {
    id: 'q-037',
    category: 'Vehicle Safety',
    categorySlug: 'vehicle-safety',
    difficulty: 'advanced',
    question: 'What is the minimum thinking distance for a car travelling at 70 mph, as shown in the Highway Code?',
    options: [
      { id: 'q-037-a', text: '14 metres (46 feet)' },
      { id: 'q-037-b', text: '21 metres (70 feet)' },
      { id: 'q-037-c', text: '24 metres (79 feet)' },
      { id: 'q-037-d', text: '18 metres (60 feet)' },
    ],
    correctOptionId: 'q-037-b',
    explanation: 'At 70 mph, the typical thinking distance is 21 metres (70 feet). The overall stopping distance at 70 mph is 96 metres (315 feet), which includes a braking distance of 75 metres (245 feet). These figures are for dry roads and a car in good condition.',
    ruleReference: 'Highway Code Rule 126 — Stopping distances table: at 70 mph, thinking distance 21m, braking distance 75m, overall 96m.',
    learningTakeaway: 'A useful memory aid: thinking distance in metres roughly equals speed in mph divided by 3.3. The overall stopping distance at 70 mph (96m) is the length of nearly 24 car lengths.',
  },

  // ─── EMERGENCY SITUATIONS (Q38–Q39) ──────────────────────────────────────
  {
    id: 'q-038',
    category: 'Emergency Situations',
    categorySlug: 'emergency-situations',
    difficulty: 'standard',
    question: 'Your vehicle breaks down on a motorway. Where should you stop and what should you do first?',
    options: [
      { id: 'q-038-a', text: 'Stop in the left-hand lane and switch on hazard lights' },
      { id: 'q-038-b', text: 'Pull onto the hard shoulder or emergency refuge area, switch on hazard lights, and exit via the left-hand door' },
      { id: 'q-038-c', text: 'Stop in the middle lane and call for assistance' },
      { id: 'q-038-d', text: 'Continue to the next junction before stopping' },
    ],
    correctOptionId: 'q-038-b',
    explanation: 'If your vehicle breaks down on a motorway, pull onto the hard shoulder or emergency refuge area as far to the left as possible. Switch on hazard lights. Exit the vehicle via the left-hand door and move behind the barrier. Call for assistance using the emergency telephone or your mobile phone. Never stand behind or near the vehicle.',
    ruleReference: 'Highway Code Rules 274–278 — If you break down on a motorway, pull onto the hard shoulder, switch on hazard lights, exit via the left door, and stand behind the barrier.',
    learningTakeaway: 'On smart motorways without a permanent hard shoulder, use emergency refuge areas (orange SOS phones). Never attempt repairs on the hard shoulder. Always stand behind the barrier, not beside the vehicle.',
  },
  {
    id: 'q-039',
    category: 'Emergency Situations',
    categorySlug: 'emergency-situations',
    difficulty: 'advanced',
    question: 'You are involved in a collision. There are no injuries but another vehicle is damaged and the driver has left the scene. What must you do?',
    options: [
      { id: 'q-039-a', text: 'Leave a note on the other vehicle and drive away' },
      { id: 'q-039-b', text: 'Report the collision to the police within 24 hours' },
      { id: 'q-039-c', text: 'Nothing — you are not at fault' },
      { id: 'q-039-d', text: 'Report the collision to the police within 48 hours' },
    ],
    correctOptionId: 'q-039-b',
    explanation: 'If you are involved in a collision that causes damage to another vehicle or property and you cannot exchange details at the scene, you must report it to the police as soon as reasonably practicable and in any case within 24 hours. Failure to do so is a criminal offence.',
    ruleReference: 'Road Traffic Act 1988 Section 170 — You must stop, exchange details, and if unable to do so, report to police within 24 hours.',
    learningTakeaway: 'Always stop after a collision. Exchange name, address, vehicle registration, and insurance details. If anyone is injured, you must also produce your insurance certificate or report to police within 24 hours.',
  },

  // ─── WEATHER CONDITIONS (Q40–Q41) ─────────────────────────────────────────
  {
    id: 'q-040',
    category: 'Weather Conditions',
    categorySlug: 'weather-conditions',
    difficulty: 'standard',
    question: 'When must you use rear fog lights?',
    options: [
      { id: 'q-040-a', text: 'Whenever it is raining heavily' },
      { id: 'q-040-b', text: 'When visibility is seriously reduced — generally below 100 metres' },
      { id: 'q-040-c', text: 'At all times during darkness' },
      { id: 'q-040-d', text: 'Only on motorways in fog' },
    ],
    correctOptionId: 'q-040-b',
    explanation: 'You must use rear fog lights when visibility is seriously reduced — generally when you cannot see for more than 100 metres (328 feet). You must switch them off when visibility improves, as they can dazzle following drivers and mask your brake lights.',
    ruleReference: 'Highway Code Rule 236 — You MUST use rear fog lights when visibility is seriously reduced (generally below 100 metres). Switch them off when visibility improves.',
    learningTakeaway: 'Fog lights are for seriously reduced visibility only. Using them in normal conditions is illegal and can dazzle other drivers. Front fog lights may also be used in seriously reduced visibility.',
  },
  {
    id: 'q-041',
    category: 'Weather Conditions',
    categorySlug: 'weather-conditions',
    difficulty: 'advanced',
    question: 'You are driving in icy conditions. How much can stopping distances increase compared to dry conditions?',
    options: [
      { id: 'q-041-a', text: 'Up to twice as long' },
      { id: 'q-041-b', text: 'Up to five times as long' },
      { id: 'q-041-c', text: 'Up to ten times as long' },
      { id: 'q-041-d', text: 'Up to three times as long' },
    ],
    correctOptionId: 'q-041-c',
    explanation: 'In icy conditions, stopping distances can be up to ten times greater than in dry conditions. This is because ice dramatically reduces tyre grip. You should drive very slowly, use the highest gear possible to reduce wheel spin, and brake and steer very gently.',
    ruleReference: 'Highway Code Rule 228 — In icy or snowy weather, stopping distances can be ten times greater than in dry conditions.',
    learningTakeaway: 'In snow and ice, drive at a very low speed, use gentle inputs on the steering, brakes, and accelerator, and leave a very large gap to the vehicle ahead. Consider whether the journey is necessary.',
  },

  // ─── PARKING RULES (Q42–Q43) ──────────────────────────────────────────────
  {
    id: 'q-042',
    category: 'Parking Rules',
    categorySlug: 'parking-rules',
    difficulty: 'foundation',
    question: 'What does a single yellow line on the road mean?',
    options: [
      { id: 'q-042-a', text: 'No parking at any time' },
      { id: 'q-042-b', text: 'No waiting during certain times shown on nearby signs or plates' },
      { id: 'q-042-c', text: 'No loading or unloading at any time' },
      { id: 'q-042-d', text: 'Parking is permitted for up to one hour' },
    ],
    correctOptionId: 'q-042-b',
    explanation: 'A single yellow line means no waiting during the times shown on nearby signs or kerb plates. Outside those times, parking may be permitted. A double yellow line means no waiting at any time. Always check nearby signs for the exact restrictions.',
    ruleReference: 'Highway Code Rule 238 — Single yellow line: no waiting during times shown on signs. Double yellow line: no waiting at any time.',
    learningTakeaway: 'Single yellow = restricted times (check signs). Double yellow = no waiting at any time. Kerb marks (yellow lines on the kerb) indicate loading/unloading restrictions.',
  },
  {
    id: 'q-043',
    category: 'Parking Rules',
    categorySlug: 'parking-rules',
    difficulty: 'standard',
    question: 'When parking at night on a road with a speed limit greater than 30 mph, what must you do?',
    options: [
      { id: 'q-043-a', text: 'Park facing the direction of traffic and use sidelights' },
      { id: 'q-043-b', text: 'Park facing the direction of traffic and switch off all lights' },
      { id: 'q-043-c', text: 'Park facing any direction as long as hazard lights are on' },
      { id: 'q-043-d', text: 'Park in a lay-by only' },
    ],
    correctOptionId: 'q-043-a',
    explanation: 'When parking on a road with a speed limit greater than 30 mph at night, you must park facing the direction of traffic flow and leave your sidelights on. This makes your vehicle visible to other road users. On roads with a 30 mph or lower limit, you may park without lights if you are close to the kerb.',
    ruleReference: 'Highway Code Rule 248 — On roads with a speed limit greater than 30 mph, you must leave sidelights on when parked at night.',
    learningTakeaway: 'On faster roads at night, sidelights help other drivers see your parked vehicle. Always park facing the direction of traffic so your reflectors face oncoming vehicles.',
  },

  // ─── HIGHWAY CODE KNOWLEDGE (Q44–Q46) ────────────────────────────────────
  {
    id: 'q-044',
    category: 'Highway Code Knowledge',
    categorySlug: 'highway-code',
    difficulty: 'standard',
    question: 'What is the minimum eyesight requirement for driving in the UK?',
    options: [
      { id: 'q-044-a', text: 'Read a number plate at 15 metres in good daylight' },
      { id: 'q-044-b', text: 'Read a number plate at 20 metres in good daylight' },
      { id: 'q-044-c', text: 'Read a number plate at 25 metres in good daylight' },
      { id: 'q-044-d', text: 'Pass a standard optician\'s eye test' },
    ],
    correctOptionId: 'q-044-b',
    explanation: 'The minimum eyesight standard for driving in the UK is being able to read a standard number plate (post-September 2001 format) at 20 metres in good daylight, with glasses or contact lenses if worn. This is checked at the start of the practical driving test.',
    ruleReference: 'Highway Code Rule 92 — You MUST be able to read a vehicle number plate from 20 metres. If you need glasses or contact lenses to do so, you must wear them whenever you drive.',
    learningTakeaway: 'If you need glasses or contact lenses to meet the eyesight standard, you must wear them every time you drive. Driving without required corrective lenses is a criminal offence.',
  },
  {
    id: 'q-045',
    category: 'Highway Code Knowledge',
    categorySlug: 'highway-code',
    difficulty: 'advanced',
    question: 'Under the updated Highway Code (2022), who has priority at a junction when a pedestrian is crossing or waiting to cross a road you are turning into?',
    options: [
      { id: 'q-045-a', text: 'The driver turning into the road' },
      { id: 'q-045-b', text: 'The pedestrian crossing or waiting to cross' },
      { id: 'q-045-c', text: 'Whoever arrives at the junction first' },
      { id: 'q-045-d', text: 'The pedestrian only if they are on a marked crossing' },
    ],
    correctOptionId: 'q-045-b',
    explanation: 'Under the updated Highway Code (January 2022), pedestrians crossing or waiting to cross a road that you are turning into have priority. Drivers must give way to them. This is a significant change from previous guidance and applies even where there is no marked crossing.',
    ruleReference: 'Highway Code Rule H2 (2022 update) — Drivers must give way to pedestrians crossing or waiting to cross a road into which they are turning.',
    learningTakeaway: 'The 2022 Highway Code update introduced a hierarchy of road users. Pedestrians have the highest priority, followed by cyclists, then motorcyclists, then cars. This change affects turning at junctions.',
  },
  {
    id: 'q-046',
    category: 'Highway Code Knowledge',
    categorySlug: 'highway-code',
    difficulty: 'standard',
    question: 'What does the term "MSM" routine stand for in the Highway Code?',
    options: [
      { id: 'q-046-a', text: 'Mirror, Signal, Manoeuvre' },
      { id: 'q-046-b', text: 'Move, Steer, Manoeuvre' },
      { id: 'q-046-c', text: 'Mirror, Speed, Move' },
      { id: 'q-046-d', text: 'Merge, Signal, Move' },
    ],
    correctOptionId: 'q-046-a',
    explanation: 'MSM stands for Mirror, Signal, Manoeuvre. This is the standard routine for any change of direction or speed. You should check your mirrors, signal your intention, and then carry out the manoeuvre safely. The full routine is often extended to Mirror-Signal-Position-Speed-Look (MSPSL).',
    ruleReference: 'Highway Code Rule 159 — Use the MSM (Mirror-Signal-Manoeuvre) routine before changing direction or speed.',
    learningTakeaway: 'MSM is the foundation of safe driving. Always check mirrors before signalling, and signal before manoeuvring. The extended MSPSL routine adds Position and Speed checks before the final Look.',
  },

  // ─── ADDITIONAL MIXED CATEGORIES (Q47–Q50) ────────────────────────────────
  {
    id: 'q-047',
    category: 'Motorways',
    categorySlug: 'motorways',
    difficulty: 'standard',
    question: 'You are driving on a motorway and see a sign showing a temporary speed limit of 50 mph on an overhead gantry. Is this limit mandatory?',
    options: [
      { id: 'q-047-a', text: 'No — it is advisory only' },
      { id: 'q-047-b', text: 'Yes — it is a mandatory limit and must be obeyed' },
      { id: 'q-047-c', text: 'Only if accompanied by a red border on the sign' },
      { id: 'q-047-d', text: 'Only during peak hours' },
    ],
    correctOptionId: 'q-047-b',
    explanation: 'Variable speed limits displayed on motorway overhead gantry signs are mandatory and must be obeyed. They are used to manage traffic flow, reduce congestion, and improve safety. Exceeding a variable speed limit is a criminal offence in the same way as exceeding a fixed speed limit.',
    ruleReference: 'Highway Code Rule 262 — Variable speed limits on motorway gantry signs are mandatory. You must not exceed the speed shown.',
    learningTakeaway: 'On smart motorways, variable speed limits are enforced by average speed cameras. Even if the road appears clear, you must comply with the displayed limit.',
  },
  {
    id: 'q-048',
    category: 'Safe Driving Practices',
    categorySlug: 'safe-driving',
    difficulty: 'foundation',
    question: 'It is illegal to use a hand-held mobile phone while driving. When is it permitted to use a hand-held phone in a vehicle?',
    options: [
      { id: 'q-048-a', text: 'When stationary in traffic' },
      { id: 'q-048-b', text: 'When parked with the engine switched off' },
      { id: 'q-048-c', text: 'When driving at speeds below 20 mph' },
      { id: 'q-048-d', text: 'When using hands-free mode' },
    ],
    correctOptionId: 'q-048-b',
    explanation: 'It is illegal to use a hand-held mobile phone while driving or while stopped with the engine running (e.g. in traffic). You may use a hand-held phone only when the vehicle is safely parked with the engine switched off. Using a hand-held phone while driving carries a £200 fine and 6 penalty points.',
    ruleReference: 'Highway Code Rule 149 — You MUST NOT use a hand-held mobile phone while driving or while supervising a learner driver. This includes when stopped at traffic lights.',
    learningTakeaway: 'Even hands-free use can distract you. The safest approach is to switch your phone to silent and place it out of reach before driving. Pull over safely and switch off the engine before using a hand-held phone.',
  },
  {
    id: 'q-049',
    category: 'Road Signs',
    categorySlug: 'road-signs',
    difficulty: 'standard',
    question: 'What does a triangular sign showing a silhouette of two children mean?',
    options: [
      { id: 'q-049-a', text: 'School crossing patrol ahead' },
      { id: 'q-049-b', text: 'School or playground ahead — children may be crossing the road' },
      { id: 'q-049-c', text: 'Children\'s play area on the left' },
      { id: 'q-049-d', text: 'Pedestrian zone begins ahead' },
    ],
    correctOptionId: 'q-049-b',
    explanation: 'A triangular warning sign showing two children (one appearing to run) warns of a school or playground ahead where children may be crossing the road. You should reduce your speed and be prepared to stop. This sign is commonly found near schools and parks.',
    ruleReference: 'Traffic Signs Regulations and General Directions 2016 — Triangular sign with children silhouette: school or playground ahead, children may be crossing.',
    learningTakeaway: 'Warning signs near schools require extra caution, especially at school start and finish times. Watch for children who may run into the road without warning.',
  },
  {
    id: 'q-050',
    category: 'Vehicle Safety',
    categorySlug: 'vehicle-safety',
    difficulty: 'standard',
    question: 'You are about to drive your car. You notice the brake fluid level is below the minimum mark. What should you do?',
    options: [
      { id: 'q-050-a', text: 'Top up the fluid and drive carefully' },
      { id: 'q-050-b', text: 'Do not drive the vehicle until the cause has been investigated and rectified' },
      { id: 'q-050-c', text: 'Drive slowly to a garage to have it checked' },
      { id: 'q-050-d', text: 'Top up with water as a temporary measure' },
    ],
    correctOptionId: 'q-050-b',
    explanation: 'If the brake fluid level is below the minimum mark, you must not drive the vehicle until the cause has been investigated and the system has been checked by a qualified mechanic. Low brake fluid can indicate a leak in the braking system, which could lead to brake failure. Never use water as a substitute for brake fluid.',
    ruleReference: 'Highway Code Rule 90 — You must ensure your vehicle is roadworthy before driving. A low brake fluid level indicates a potential braking system fault.',
    learningTakeaway: 'Brake fluid level dropping below minimum is a warning sign of a potential leak. Do not simply top up and drive — have the system inspected. Brake failure at speed can be fatal.',
  },
];